import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAllPointOfInterestTemplateQuery } from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
/**
 * Hook that fetches POI templates via GraphQL and populates global state with POI type mappings.
 * Should be called once at a parent component level (e.g., InsightsExplorer).
 * Child components can access the mappings from global state using useAtomValue.
 *
 * Populates:
 *   - analysisState.poiTypeToColorMap: Map of POI type (lowercase) to color hex
 *   - analysisState.poiTypeToIconMap: Map of POI type (lowercase) to icon URL
 */
export const usePoiTypeMapping = () => {
  const structureId = useGetStructureId();
  const assemblyTemplateId = useSelectedAssemblyTemplateId();
  const setPoiTypeToColorMap = useSetAtom(analysisState.poiTypeToColorMap);
  const setPoiTypeToIconMap = useSetAtom(analysisState.poiTypeToIconMap);

  const { data } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId,
      assemblyTemplateId: assemblyTemplateId ?? '',
    },
  });

  // Build maps and populate global state when data is available
  useEffect(() => {
    if (!data?.allPointOfInterestTemplate) return;

    const iconMap = new Map<string, string>();
    const colorMap = new Map<string, string>();

    const templates = data.allPointOfInterestTemplate;

    if (templates.length > 0) {
      templates.forEach((template) => {
        const templateKey = template.key?.toLowerCase() || template.name.toLowerCase();
        const iconName = template.icon?.name;
        const color = template.icon?.color || template.accentColor;

        if (iconName) {
          iconMap.set(templateKey, iconName);
        }

        if (color) {
          colorMap.set(templateKey, color);
        }
      });

      // Populate global state
      setPoiTypeToIconMap(iconMap);
      setPoiTypeToColorMap(colorMap);
    }
  }, [data, setPoiTypeToIconMap, setPoiTypeToColorMap]);
};
